import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../lib/lang";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const { t } = useLang();

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const id = setInterval(() => setIdx((v) => (v + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [items]);

  if (items.length === 0) return null;
  const tt = items[idx];

  return (
    <section
      data-testid="testimonials-section"
      className="bg-[#F5F2EB] section-py"
    >
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="overline text-[#B08858]">{t("testi.overline")}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#0A0A0A]">
              {t("testi.title")}
            </h2>
            <div className="mt-10 flex gap-3">
              <button
                onClick={() => setIdx((v) => (v - 1 + items.length) % items.length)}
                data-testid="testimonial-prev"
                className="w-12 h-12 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F5F2EB] flex items-center justify-center transition"
                aria-label={t("common.previous")}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setIdx((v) => (v + 1) % items.length)}
                data-testid="testimonial-next"
                className="w-12 h-12 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F5F2EB] flex items-center justify-center transition"
                aria-label={t("common.next")}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className="md:col-span-7 relative min-h-[280px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={tt.id || idx}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6 }}
                className="relative"
                data-testid={`testimonial-${idx}`}
              >
                <Quote size={64} className="text-[#B08858]/30 mb-4" />
                <p className="font-display text-2xl md:text-3xl lg:text-4xl text-[#0A0A0A] leading-snug">
                  &ldquo;{tt.quote}&rdquo;
                </p>
                <div className="mt-8 pt-6 border-t border-[#D7CFC4] flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="font-display text-xl text-[#0A0A0A]">{tt.name}</p>
                    <p className="text-sm text-[#0A0A0A]/60">{tt.location}</p>
                  </div>
                  <div className="flex gap-1 text-[#B08858]" role="img" aria-label={`${tt.rating || 5} out of 5 stars`}>
                    {/* Rendering a fixed-count star row — positional keys are stable here. */}
                    {Array.from({ length: tt.rating || 5 }).map((_, i) => (
                      <Star key={`star-${i}`} size={16} fill="currentColor" />
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
